import { ActionIcon, Button, Center, Checkbox, Group, Pagination, Skeleton, Table } from '@mantine/core';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { closeAllModals, openModal } from '@mantine/modals';
import { IconPencil, IconTrashX } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FAQsPropertyItem, useFAQsPropertyList, useFAQsPropertyMethods } from '@mygpt/app';
import { SvgAdd, SvgTrash } from '@/components/Common/icon';
import { FaqsPropertyItemEdit, FaqsPropertyItemEditProps } from '@/components/Faqs/property-edit';
import { FaqsPropertyItemRemove } from '@/components/Faqs/property-remove';
import ContainerHeader from '@/components/Headers/ContainerHeader';
import { useDatasetStore } from '@/store/dataset';

import classes from './property.module.css';
import { useParams } from 'react-router-dom';

interface FaqsPropertyItemProps {
  item: FAQsPropertyItem;
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  onDelete?: (faqs: FAQsPropertyItem) => void;
  onEdit?: (faqs: FAQsPropertyItem) => void;
}

const FaqsPropertItem: FC<FaqsPropertyItemProps> = ({ item, onDelete, onEdit, onSelectedChange, selected }) => {
  return (
    <Table.Tr
      className="cursor-pointer"
      onClick={() => {
        onSelectedChange?.(!selected);
      }}
    >
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Checkbox checked={selected} onChange={() => {}} />
          <span className="text-left">{item.title}</span>
        </Group>
      </Table.Td>
      <Table.Td>{item.type}</Table.Td>
      <Table.Td>{item.description}</Table.Td>
      <Table.Td className="hidden sm:block">{dayjs(item.updated_at).format('YYYY-MM-DD HH:mm:ss')}</Table.Td>
      <Table.Td>
        <Center>
          <Group gap={0} wrap="nowrap">
            <ActionIcon
              size="sm"
              variant="subtle"
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(item);
              }}
            >
              <IconPencil size={14} />
            </ActionIcon>
            <ActionIcon
              size="sm"
              variant="subtle"
              color="red"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(item);
              }}
              className="text-violet-500"
            >
              <IconTrashX size={14} color="red" />
            </ActionIcon>
          </Group>
        </Center>
      </Table.Td>
    </Table.Tr>
  );
};

export const FaqsPropertyList = () => {
  const { t } = useTranslation();
  const matches = useMediaQuery('(min-width: 640px)');

  const { datasetId } = useParams();
  const { data: propertyList, loadList, loading, page, setPage } = useFAQsPropertyList(datasetId);
  const [search] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 500);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { addFAQsProperty, batchDeleteFAQsProperty, updateFAQsProperty } = useFAQsPropertyMethods(datasetId);
  const isLoadedRef = useRef(false);

  const { isCloning, setCloningTipOpened, setCount } = useDatasetStore();

  const allSelected = useMemo(
    () => selectedIds.length > 0 && propertyList?.length === selectedIds.length,
    [propertyList, selectedIds]
  );

  const toggleAllChecked = useCallback(
    (checked: boolean) => {
      setSelectedIds(checked ? propertyList.map((item) => item.id) : []);
    },
    [propertyList]
  );

  const selectFAQsProperty = useCallback((selectFAQsProperty: FAQsPropertyItem) => {
    setSelectedIds((ids) => {
      if (ids.includes(selectFAQsProperty.id)) {
        return ids.filter((id) => id !== selectFAQsProperty.id);
      }
      return [...ids, selectFAQsProperty.id];
    });
  }, []);

  useLayoutEffect(() => {
    if (debouncedSearch) {
      // searchList({
      //   question: debouncedSearch,
      // });
    } else if (isLoadedRef.current && datasetId) {
      loadList({ page: 1 });
    }
  }, [datasetId, debouncedSearch, loadList]);

  useEffect(() => {
    isLoadedRef.current = true;
  }, []);

  useEffect(() => {
    setSelectedIds([]);
  }, [debouncedSearch, datasetId, page.page, page.size]);

  useEffect(() => {
    setCount('property', page.total);
  }, [page.total]);

  const handleEdit = (property?: FAQsPropertyItem) => {
    if (isCloning) {
      setCloningTipOpened(true);
      return;
    }
    const isEdit = !!property;
    const onSubmit = async (params: Parameters<NonNullable<FaqsPropertyItemEditProps['onSubmit']>>[0]) => {
      if (isEdit) {
        await updateFAQsProperty({ datasetId, propertyId: property.id, ...params });
      } else {
        await addFAQsProperty({ datasetId, ...params });
      }
      closeAllModals();
    };
    openModal({
      title: property ? t('faqs.property.edit_title') : t('faqs.property.add_title'),
      withinPortal: false,
      centered: true,
      radius: 'md',
      size: 'lg',
      children: <FaqsPropertyItemEdit propetry={property} onSubmit={onSubmit} />,
    });
  };

  const handleDelete = (...ids: string[]) => {
    if (isCloning) {
      setCloningTipOpened(true);
      return;
    }
    const onDelete = async (params: Parameters<typeof batchDeleteFAQsProperty>[0]) => {
      await batchDeleteFAQsProperty(params);
      const updatedSelectedIds = selectedIds.filter((item) => !params.propertyIds.includes(item));
      setSelectedIds(updatedSelectedIds);
    };
    openModal({
      title: t('faqs.property.delete.title'),
      withinPortal: false,
      centered: true,
      radius: 'md',
      children: <FaqsPropertyItemRemove datasetId={datasetId} propertyIds={ids} onDelete={onDelete} />,
    });
  };

  const renderContent = () => {
    if (loading) {
      return (
        <section className="bg-violet-100 px-5 py-5 rounded-md">
          {new Array(2).fill('').map((item, index) => (
            <div key={index} className={index ? 'mt-5' : ''}>
              <Skeleton className={classes.skeleton} width={'40%'} height={15} radius="sm" />
              <div className="ml-5">
                {new Array(5).fill('').map((item, indexNum) => (
                  <Skeleton
                    key={indexNum}
                    className={classes.skeleton}
                    bg={'#000'}
                    mt={12}
                    width={`${Math.floor(Math.random() * 70 + 30)}%`}
                    height={10}
                    radius="sm"
                  />
                ))}
              </div>
            </div>
          ))}
        </section>
      );
    }
    return (
      <Table highlightOnHover className={classes.table} captionSide="bottom">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <Group gap="xs">
                <Checkbox checked={allSelected} onChange={() => toggleAllChecked(!allSelected)} />
                {t('faqs.property.field.title')}
              </Group>
            </Table.Th>
            <Table.Th>{t('faqs.property.field.type')}</Table.Th>
            <Table.Th>{t('faqs.property.field.description')}</Table.Th>
            <Table.Th className="hidden sm:block">{t('faqs.property.field.last_change')}</Table.Th>
            <Table.Th>{t('faqs.property.field.action')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {propertyList.map((item, index) => (
            <FaqsPropertItem
              item={item}
              key={index}
              selected={selectedIds.includes(item.id)}
              onSelectedChange={() => selectFAQsProperty(item)}
              onEdit={handleEdit}
              onDelete={(item) => handleDelete(item.id)}
            />
          ))}
        </Table.Tbody>
        {!debouncedSearch && page.pages > 1 && (
          <caption className="bg-white mt-0 pt-3">
            <Center className="mb-4">
              <Pagination
                value={page.page}
                size={matches ? 'md' : 'sm'}
                onChange={(value) => setPage({ page: value })}
                total={page.pages}
                color="violet.5"
              />
            </Center>
          </caption>
        )}
      </Table>
    );
  };

  return (
    <div>
      <ContainerHeader description={t('faqs.property.description')} gap={0} />
      <div className="flex justify-between mb-5">
        <Group>
          <Button leftSection={<SvgAdd />} onClick={() => handleEdit()}>
            {t('faqs.property.action.add')}
          </Button>
        </Group>
        <div className="flex flex-1 gap-3 items-center justify-end">
          <Button
            color="red"
            leftSection={<SvgTrash />}
            disabled={selectedIds.length === 0}
            variant="outline"
            className="filled"
            onClick={() => handleDelete(...selectedIds)}
          >
            {t('faqs.property.action.delete')}
          </Button>
          {/* <Button leftSection={<IconDatabaseExport />} variant="outline">
            Export
          </Button> */}
          {/* <Input
            className="min-w-[20%]"
            icon={<IconSearch />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            placeholder={t('create.faqs.search_placeholder')}
          /> */}
        </div>
      </div>
      {renderContent()}
    </div>
  );
};
